<template>
  <div class="pivot-vxe-table" :class="exportClass" :style="'height:' + height + ';width:' + width">
    <div v-if="loading" class="empty">加载中...</div>
    <div v-else class="table">
      <jsx :render-fuc="renderTable"></jsx>
    </div>
    <el-pagination
      small
      class="table-pagination"
      layout="prev, pager, next"
      :total="pageConfig.total"
      :current-page.sync="pageConfig.page"
      :page-size="pageConfig.size"
      @current-change="pageChange"
    ></el-pagination>
  </div>
</template>

<script>
import ManyKeysMap from "many-keys-map"
import { isEmpty, pick, sumBy, max, min, meanBy, keyBy } from "lodash"
import Jsx from "@/components/Jsx"
import { addToMap, deepClone, getDealData, randomString } from "@/utils/common"
import DropdownMenu from "@/components/UIComponents/DropdownMenu"
import naturalSort from "javascript-natural-sort"
import ChartMixin from "@/views/Backend/Admin/Statistics/chartEdit/ChartMixin"
import { sortDataTitle } from "@/api/bi"
import { gradient } from "@/views/Backend/Admin/Layout/mixin/gradientColor"
import {
  sortDirectionMap,
  sortIconMap,
  menuList,
  colorList
} from "@/views/Backend/Admin/Statistics/chartEdit/static"
import { firstBy } from "thenby"

export function getNumberData(arr) {
  return arr.map(item => {
    if (Number.isNaN(item)) return item
    const dataInfo = item?.toString().replace(/,/g, "")
    return dataInfo.includes("%") ? parseFloat(dataInfo) / 100 : Number(dataInfo)
  })
}

const methodMap = {
  SUM: (arr, unit, showThousands) => {
    arr = getNumberData(arr)
    return getDealData(unit, sumBy(arr), showThousands)
  },
  AVG: (arr, unit, showThousands) => {
    arr = getNumberData(arr)
    return getDealData(unit, meanBy(arr), showThousands)
  },
  MAX: (arr, unit, showThousands) => {
    arr = getNumberData(arr)
    return getDealData(unit, max(arr), showThousands)
  },
  MIN: (arr, unit, showThousands) => {
    arr = getNumberData(arr)
    return getDealData(unit, min(arr), showThousands)
  },
  COUNT: (arr, unit, showThousands) => {
    return getDealData(unit, arr.length, showThousands)
  }
}

export default {
  name: "PivotVxeTable",
  // eslint-disable-next-line vue/no-unused-components
  components: { Jsx, DropdownMenu },
  props: {
    rawData: {
      type: Array,
      default: () => []
    },
    xAxisList: {
      type: Array,
      default: () => []
    },
    yAxisList: {
      type: Array,
      default: () => []
    },
    summaryConfig: {
      type: Array,
      default: () => []
    },
    summaryPosition: Object,
    rules: {
      type: Array,
      default: () => []
    },
    defaultSort: Object,
    showNumber: Boolean,
    appId: String,
    sheetId: String,
    showRawData: Boolean,
    quotas: Array
  },
  data() {
    return {
      sortDirectionMap,
      sortIconMap,
      menuList,
      colorList,
      loading: false,
      colDefs: [],
      tableData: [],
      mergeCells: [],
      newColIdMap: {},
      customSort: {
        key: "",
        direction: "",
        internal: true
      },
      pageConfig: {
        total: 0,
        page: 1,
        size: 20
      },
      width: "",
      height: "",
      exportClass: "",
      exportExcel: false
    }
  },
  mixins: [ChartMixin],
  computed: {
    colCids() {
      return this.xAxisList.filter(t => t.type === "COLUMN").map(col => col.id)
    },
    rowCids() {
      return this.xAxisList.filter(t => t.type === "ROW").map(col => col.id)
    },
    axisMap() {
      return keyBy(this.xAxisList.concat(this.yAxisList), "id")
    },
    noCalcIds() {
      let result = []
      this.yAxisList.forEach(yAxis => {
        if (
          yAxis.extraCalc &&
          (!["HB", "TB"].includes(yAxis.extraCalc) ||
            (["HB", "TB"].includes(yAxis.extraCalc) &&
              ["NORMAL", "RATIO"].includes(yAxis?.thbConfig?.dataType)))
        ) {
          result = result.concat(this.newColIdMap[yAxis.id])
        }
      })
      return result
    },
    hiddenColIds() {
      let result = []
      this.yAxisList
        .filter(yAxis => yAxis.hidden === true)
        .forEach(hiddenYAxis => {
          result.push(hiddenYAxis.id)
          result.push(...(this.newColIdMap[hiddenYAxis.id] || []))
        })
      return result
    },
    newColIdAxisMap() {
      let result = {}
      this.yAxisList.forEach(yAxis => {
        if (yAxis.type === "FUNC" && !["VALUE", "DIFF"].includes(yAxis?.thbConfig?.dataType)) {
          this.newColIdMap[yAxis.id].forEach(newColId => {
            result[newColId] = deepClone(yAxis)
            let newColIdInfo = newColId.split("_")
            let allFields = this._.uniq(
              yAxis.expression.split(/[(,)]/).filter(item => item.includes("$"))
            )
            allFields.forEach(field => {
              let fieldId = field.slice(1)
              let newColIds = this.newColIdMap[fieldId]
              let curColId = newColIds.find(item => item.includes(newColIdInfo[0]))
              result[newColId] = Object.assign({}, yAxis, {
                expression: this._.replace(result[newColId].expression, fieldId, curColId)
              })
            })
          })
        }
      })
      return result
    }
  },
  mounted() {
    window.add = (a, b) => a + b
    window.subtract = (a, b) => a - b
    window.multiply = (a, b) => a * b
    window.divide = (a, b) => a / b
  },
  methods: {
    async init() {
      this.loading = true
      await this.$nextTick()
      this.colIdMap = new ManyKeysMap()
      this.colDefs = await this.calcColDef(this.rawData, this.colIdMap)
      if (!this._.isEmpty(this.defaultSort)) {
        if (this._.keys(this.newColIdMap).includes(this.defaultSort.key)) {
          let id = this.newColIdMap[this.defaultSort.key][0]
          this.customSort = Object.assign({}, this.defaultSort, { key: id })
        }
      }
      this.tableData = Object.freeze(this.calcTableData(this.rawData, this.colIdMap))
      this.mergeCells = this.calcRows(this.tableData, this.rowCids, 0, 0, this.tableData.length)
      this.loading = false
    },
    async refreshData() {
      this.loading = true
      await this.$nextTick()
      const colIdMap = this.colIdMap
      this.tableData = Object.freeze(this.calcTableData(this.rawData, colIdMap))
      this.mergeCells = this.calcRows(this.tableData, this.rowCids, 0, 0, this.tableData.length)
      this.loading = false
    },
    async dealExportImage(from) {
      await this.$nextTick()
      // 处理滚动
      this.$refs.xTable.$refs.tableBody.$el.scrollTop = 0
      this.$refs.xTable.$refs.tableBody.$el.scrollLeft = 0
      // 处理宽高
      let tableBody = this.$refs.xTable.$refs.tableBody.$refs.table
      let tableHeader = this.$refs.xTable.$refs.tableHeader.$refs.table
      this.exportClass = from === "before" ? "export-class" : ""
      this.width = from === "before" ? this.$refs.xTable.tableWidth + 30 + "px" : ""
      this.height =
        from === "before" ? tableHeader.clientHeight + tableBody.clientHeight + 60 + "px" : ""
      await this.$nextTick()
    },
    async dealExportExcel(from) {
      this.pageConfig =
        from === "before"
          ? {
              page: 1,
              size: this.pageConfig.total,
              total: this.pageConfig.total
            }
          : {
              page: 1,
              size: 20,
              total: this.pageConfig.total
            }
      this.exportExcel = from === "before"
      await this.init()
      this.loading = true
      await this.$nextTick()
      this.colDefs = this.colDefs.map(col => {
        if (col.isLeft) {
          from === "before" ? delete col.fixed : (col.fixed = "left")
        }
        return col
      })
      this.loading = false
      await this.$nextTick()
    },
    pageChange(event) {
      this.pageConfig.page = event
      this.refreshData()
    },
    async calcColDef(tableData, colIdMap) {
      this.newColIdMap = {}
      const xMap = new ManyKeysMap()
      tableData.forEach(row => {
        let colVals = this.colCids.map(id => row[id])
        this.yAxisList.forEach(({ id, name }) => {
          if (!xMap.has(colVals)) {
            xMap.set(colVals, randomString(10))
          }
          const key = colVals.concat(name)
          if (!colIdMap.has(key)) {
            // 区分分组
            let newId = xMap.get(colVals) + "_" + randomString(10)
            colIdMap.set(key, newId)
            addToMap(this.newColIdMap, id, newId)
          }
        })
      })
      let arr = Array.from(colIdMap.keys())
      let leftCols = this.xAxisList
        .filter(t => t.type === "ROW")
        .map(({ id, name }) => ({ field: id, title: name }))
      this.xAxisList
        .filter(t => t.type === "COLUMN")
        .reverse()
        .forEach(({ id, name }) => (leftCols = [{ title: name, children: leftCols }]))
      leftCols.forEach(col => {
        col.fixed = "left"
        col.isLeft = true
      })
      if (!this._.isEmpty(this.xAxisList.filter(t => t.type === "COLUMN"))) {
        arr = this.sortArr(arr)
      }
      const rightCols = await sortDataTitle(
        (await this.calcCols(arr, 0, arr.length, colIdMap, 0)) || []
      )
      // 列汇总
      let summaryCols = this.summaryConfig
        .filter(t => t.type == "ROW")
        .flatMap(({ id, name, method }) => {
          let summaryCol = this.yAxisList
            .filter(
              item =>
                !["HB", "TB"].includes(item.extraCalc) ||
                (["HB", "TB"].includes(item.extraCalc) &&
                  ["VALUE", "DIFF"].includes(item?.thbConfig?.dataType))
            )
            .map(({ id: axisId, name }) => ({
              field: `${id}_${axisId}`,
              title: name,
              isSummaryCol: true
            }))
          const column = this.xAxisList.filter(t => t.type === "COLUMN")
          column.forEach(
            () => (summaryCol = { title: "-", isSummaryCol: true, children: [].concat(summaryCol) })
          )
          // 没有为列的维度时，处理成：总计_指标
          if (isEmpty(column)) {
            summaryCol = summaryCol.map(t => {
              t.title = name + "_" + t.title
              t.isSummaryCol = true
              return t
            })
            return summaryCol
          } else {
            summaryCol.title = name
            summaryCol.isSummaryCol = true
            return summaryCol
          }
        })
      return this.summaryPosition.rowTotalPosition === "left"
        ? [...leftCols, ...summaryCols, ...rightCols]
        : [...leftCols, ...rightCols, ...summaryCols]
    },
    sortArr(arr) {
      const showXColumns = this.xAxisList.filter(xAxis => xAxis.type === "COLUMN")
      let composedSortFunction
      showXColumns.forEach((xColumn, index) => {
        if (index === 0) {
          composedSortFunction = firstBy(0, { cmp: xColumn.sort || naturalSort })
        } else {
          composedSortFunction = composedSortFunction.thenBy(index, {
            cmp: xColumn.sort || naturalSort
          })
        }
      })
      return [...arr].sort(composedSortFunction)
    },
    calcTableData(tableData, colIdMap) {
      const dataColIds = Array.from(colIdMap.values())
      // 合并行
      tableData = this.mergeRows(tableData, colIdMap)
      // 数据总计
      this.pageConfig.total = tableData.length
      // 排序
      tableData = this.sortData(tableData)
      const allTableData = deepClone(tableData)
      // 分页
      const { page, size } = this.pageConfig
      tableData = tableData.slice((page - 1) * size, page * size)

      // 列分组汇总
      if (this.customSort.internal) this.summarySubRow(tableData, dataColIds)

      // 列汇总
      this.summaryRow(tableData, dataColIds, allTableData)

      // 行汇总
      this.summaryCol(tableData)

      return tableData
    },
    calcRows(tableData, rowCids, curColIdx, rowTopIdx, rowBottomIdx) {
      if (rowTopIdx >= rowBottomIdx || curColIdx >= rowCids.length) return []
      let res = []
      let lastVal = tableData[rowTopIdx][rowCids[curColIdx]]
      let lastIdx = rowTopIdx
      for (let i = rowTopIdx + 1; i < rowBottomIdx; i++) {
        const curVal = tableData[i][rowCids[curColIdx]]
        if (lastVal != curVal) {
          if (i - lastIdx > 1) {
            res.push({ row: lastIdx, col: curColIdx, rowspan: i - lastIdx, colspan: 1 })
            res.push(...this.calcRows(tableData, rowCids, curColIdx + 1, lastIdx, i))
          }
          lastVal = curVal
          lastIdx = i
        }
      }
      if (rowBottomIdx - lastIdx > 1) {
        res.push({ row: lastIdx, col: curColIdx, rowspan: rowBottomIdx - lastIdx, colspan: 1 })
        res.push(...this.calcRows(tableData, rowCids, curColIdx + 1, lastIdx, rowBottomIdx))
      }
      return this.showNumber
        ? res.map(item => {
            item.col = item.col + 1
            return item
          })
        : res
    },
    getSortFuncDirection(axisId) {
      return this.customSort[axisId]?.direction || this.axisMap[axisId]?.sortDirection || "ASC"
    },
    getNumber(data) {
      if ([null, undefined].includes(data)) return -Infinity
      if (Number.isNaN(data)) return data
      const dataInfo = data?.toString().replace(/,/g, "")
      return dataInfo.includes("%") ? parseFloat(data) / 100 : Number(dataInfo)
    },
    sortData(rawData) {
      if (!rawData.length) return rawData
      const { direction, key, internal, group } = this.customSort
      let sortFunc
      if (direction && direction !== "NONE") {
        sortFunc = direction.includes("ASC")
          ? (a, b) => naturalSort(this.getNumber(a[key]), this.getNumber(b[key]))
          : (a, b) => naturalSort(this.getNumber(b[key]), this.getNumber(a[key]))
      }
      if (!internal) {
        rawData.sort(sortFunc)
        return rawData
      }
      const sortedData = []
      let tmpData = []
      let rowCids = []
      const groupIndex = this.rowCids.findIndex(cid => cid === group)
      if (groupIndex !== -1) {
        rowCids = this.rowCids.slice(0, groupIndex + 1)
      } else {
        rowCids = this.rowCids.slice(0, this.rowCids.length - 1)
      }

      for (let i = 0; i < rawData.length - 1; i++) {
        const row = rawData[i]
        const nextRow = rawData[i + 1]

        let same = true
        for (const rowCid of rowCids) {
          if (row[rowCid] != nextRow[rowCid]) {
            same = false
            break
          }
        }
        tmpData.push(row)
        if (!same) {
          tmpData.sort(sortFunc)
          sortedData.push(...tmpData)
          tmpData = []
        }
      }
      tmpData.push(rawData[rawData.length - 1])
      tmpData.sort(sortFunc)
      sortedData.push(...tmpData)
      return sortedData
    },
    mergeRows(rawTableData, colIdMap) {
      const tableValMap = new ManyKeysMap()
      rawTableData.forEach(row => {
        const key = this.rowCids.map(cid => row[cid])
        // 保留行维度的值
        const item = tableValMap.get(key) || pick(row, this.rowCids)
        // 计算指标值
        let colVals = this.colCids.map(id => row[id])
        for (const yAxis of this.yAxisList) {
          const key = colVals.concat(yAxis.name)
          item[colIdMap.get(key)] = getDealData(
            this.getFormat(yAxis),
            row[yAxis.id],
            yAxis.showThousands
          )
          item[colIdMap.get(key) + "_ids"] = row.ids
        }
        tableValMap.set(key, item)
      })
      return Array.from(tableValMap.values())
    },
    summarySubRow(tableData, dataColIds) {
      const fieldSummaryMap = {}
      this.summaryConfig
        .filter(t => t.type == "COLUMN" && t.axisId)
        .forEach(t => addToMap(fieldSummaryMap, t.axisId, t))
      const lastRowIdMap = {}
      for (let i = 0; i < tableData.length; i++) {
        const curRow = tableData[i]
        if (curRow.isSummaryRow) continue
        const nextRow = tableData[i + 1]
        let insertedCount = 0
        for (let j = this.rowCids.length - 2; j >= 0; j--) {
          const fieldId = this.rowCids[j]
          let isSame = true
          if (nextRow) {
            for (let k = 0; k <= j; k++) {
              const f = this.rowCids[k]
              isSame = isSame && curRow[f] === nextRow[f]
            }
          } else {
            isSame = false
          }
          if (!isSame) {
            const configs = fieldSummaryMap[fieldId] || []
            configs.forEach(({ name, method, unit, showThousands }) => {
              const summaryRow = Object.assign(pick(curRow, this.rowCids.slice(0, j + 1)), {
                isSummaryRow: true,
                [this.rowCids[j + 1]]: name
              })
              const reduceMethod = methodMap[method]
              const rows = tableData
                .slice(lastRowIdMap[fieldId] || 0, i + 1)
                .filter(({ isSummaryRow }) => !isSummaryRow)
              for (const id of dataColIds.filter(id => this._.isEmpty(this.newColIdAxisMap[id]))) {
                if (this.noCalcIds.includes(id)) {
                  summaryRow[id] = null
                } else {
                  let arr = this.getValuableArr(rows, id)
                  summaryRow[id] = reduceMethod(arr, unit, showThousands)
                }
              }
              for (const id of dataColIds.filter(id => !this._.isEmpty(this.newColIdAxisMap[id]))) {
                if (this.noCalcIds.includes(id)) {
                  summaryRow[id] = null
                } else {
                  let yAxis = this.newColIdAxisMap[id]
                  const format = this.getFormat(yAxis)
                  summaryRow[id] = this.getComputeData(
                    summaryRow,
                    yAxis.expression,
                    format,
                    yAxis.showThousands
                  )
                }
              }
              tableData.splice(i + 1 + insertedCount++, 0, summaryRow)
            })
            lastRowIdMap[fieldId] = i + 1
          }
        }
      }
    },
    getValuableArr(arr, id) {
      return (id
        ? arr.map(item => (this._.keys(item).includes(id) ? item[id] : null))
        : arr
      ).filter(item => item !== null && item !== undefined)
    },
    summaryCol(tableData) {
      this.summaryConfig
        .filter(t => t.type == "ROW")
        .map(({ id, method, unit, showThousands }) => {
          tableData.forEach(row => {
            for (const { id: axisId } of this.yAxisList.filter(
              yAxis =>
                yAxis.type === "NORMAL" ||
                (yAxis.type === "FUNC" && ["VALUE", "DIFF"].includes(yAxis?.thbConfig?.dataType))
            )) {
              const fieldId = `${id}_${axisId}`
              const newColIds = this.newColIdMap[axisId] || []
              const reduceMethod = methodMap[method]
              let arr = this.getValuableArr(Object.values(pick(row, newColIds)))
              row[fieldId] = reduceMethod(arr, unit, showThousands)
            }
            for (const yAxis of this.yAxisList.filter(
              yAxis =>
                yAxis.type === "FUNC" && !["VALUE", "DIFF"].includes(yAxis?.thbConfig?.dataType)
            )) {
              const fieldId = `${id}_${yAxis.id}`
              const format = this.getFormat(yAxis)
              row[fieldId] = this.getComputeData(row, yAxis.expression, format, yAxis.showThousands)
            }
          })
        })
    },
    getComputeData(row, expression, format, showThousands) {
      let allFields = this._.uniq(expression.split(/[(,)]/).filter(item => item.includes("$")))
      let rowKeys = this._.keys(row)
      allFields.forEach(field => {
        let fieldId = field.slice(1)
        let rowFieldId = rowKeys.find(key => key.includes(fieldId))
        window[field] = this.getNumber(row[rowFieldId])
      })
      let result = eval(expression)
      return [Infinity, NaN].includes(result) ? "-" : getDealData(format, result, showThousands)
    },
    summaryRow(pageTableData, dataColIds, allTableData) {
      const summaryColIds = this.summaryConfig
        .filter(t => t.type == "ROW")
        .flatMap(({ id }) => this.yAxisList.map(yAxis => yAxis.id).map(axisId => `${id}_${axisId}`))
      const summaryRows = this.summaryConfig
        .filter(t => t.type == "COLUMN" && !t.axisId)
        .map(({ name, method, unit, range, showThousands }) => {
          const summaryRow = { [this.rowCids[0]]: name, isSummaryRow: true }
          const reduceMethod = methodMap[method]
          const allColIds = dataColIds.concat(summaryColIds)
          for (const id of allColIds.filter(id => this._.isEmpty(this.newColIdAxisMap[id]))) {
            if (this.noCalcIds.includes(id)) {
              summaryRow[id] = null
            } else {
              const data = range === "ALL" ? allTableData : pageTableData
              let arr = this.getValuableArr(
                data.filter(item => !item.isSummaryRow),
                id
              )
              summaryRow[id] = reduceMethod(arr, unit, showThousands)
            }
          }
          for (const id of allColIds.filter(id => !this._.isEmpty(this.newColIdAxisMap[id]))) {
            if (this.noCalcIds.includes(id)) {
              summaryRow[id] = null
            } else {
              let yAxis = this.newColIdAxisMap[id]
              const format = this.getFormat(yAxis)
              summaryRow[id] = this.getComputeData(
                summaryRow,
                yAxis.expression,
                format,
                yAxis.showThousands
              )
            }
          }
          return summaryRow
        })
      this.summaryPosition.columnTotalPosition === "bottom"
        ? pageTableData.push(...summaryRows)
        : pageTableData.unshift(...summaryRows)
    },
    async calcCols(arr, colLeftIdx, colRightIdx, colIdMap, curRowIdx) {
      if (colLeftIdx >= colRightIdx) return null
      if (curRowIdx == arr[0].length - 1) {
        let res = []
        for (let i = colLeftIdx; i < colRightIdx; i++) {
          res.push({ field: colIdMap.get(arr[i]), title: arr[i][curRowIdx], isQuota: true })
        }
        return res
      }
      let res = []
      let lastVal = arr[colLeftIdx][curRowIdx]
      let lastIdx = colLeftIdx
      for (let i = colLeftIdx + 1; i < colRightIdx; i++) {
        const curVal = arr[i][curRowIdx]
        if (lastVal != curVal) {
          res.push({
            title: lastVal,
            direction:
              this.sortDirectionMap[this.getSortFuncDirection(this.colCids[curRowIdx])] || 0,
            children: await this.calcCols(arr, lastIdx, i, colIdMap, curRowIdx + 1)
          })
          lastIdx = i
          lastVal = curVal
        }
      }
      res.push({
        title: lastVal,
        children: await this.calcCols(arr, lastIdx, colRightIdx, colIdMap, curRowIdx + 1)
      })
      return res
    },
    renderTable() {
      return (
        <vxe-table
          border
          resizable
          show-overflow="ellipsis"
          show-header-overflow="ellipsis"
          ref="xTable"
          auto-resize
          height="auto"
          column-config={{ resizable: true }}
          merge-cells={this.mergeCells}
          sort-config={{ trigger: "cell" }}
          row-config={{ height: 36 }}
          data={this.tableData}
          size="small"
          v-ps={".vxe-table--main-wrapper .vxe-table--body-wrapper"}
        >
          {this.renderNumber()}
          {this.renderCol(this.colDefs)}
        </vxe-table>
      )
    },
    renderNumber() {
      return this.showNumber ? (
        <vxe-column
          type="seq"
          width="60px"
          min-width="50px"
          title="序号"
          align="left"
          fixed="left"
          scopedSlots={{
            default: ({ row, rowIndex }) => {
              if (!row.isSummaryRow)
                return (
                  <div class="cell">
                    <div class="value" v-tip>
                      {this.getSeq(rowIndex)}
                    </div>
                  </div>
                )
            }
          }}
        ></vxe-column>
      ) : null
    },
    getSeq(rowIndex) {
      const columnTotalCount =
        this.tableData.slice(0, rowIndex).filter(data => data.isSummaryRow).length || 0
      return (this.pageConfig.page - 1) * this.pageConfig.size + rowIndex - columnTotalCount + 1
    },
    renderCol(colDefs) {
      return colDefs.map(({ field, title, fixed, children, isQuota, isSummaryCol }) => {
        const fieldInfo = field?.split("_")
        return isEmpty(children) ? (
          !(
            this.hiddenColIds.includes(field) ||
            (fieldInfo && this.hiddenColIds.includes(fieldInfo[fieldInfo?.length - 1]))
          ) ? (
            <vxe-column
              field={field}
              title={title}
              fixed={fixed}
              align={this.getPosition(title, field, children)}
              min-width="90px"
              scopedSlots={{
                default: ({ row }) => {
                  return this.renderCell(row, isSummaryCol, field, isQuota)
                },
                header: () => {
                  return this.renderHeader(field, title, isQuota, isSummaryCol)
                }
              }}
            ></vxe-column>
          ) : null
        ) : (
          <vxe-colgroup
            field={field}
            title={title}
            fixed={fixed}
            align="center"
            header-class-name={isSummaryCol ? "summary" : ""}
            scopedSlots={{
              header: () => {
                return this.renderHeader(field, title, isQuota, isSummaryCol)
              }
            }}
          >
            {this.renderCol(children)}
          </vxe-colgroup>
        )
      })
    },
    getClass(row, isSummaryCol) {
      return row.isSummaryRow || isSummaryCol ? ["summary", "cell"] : ["cell"]
    },
    getValueClass(row, field, isQuota) {
      return this.showRawData && isQuota && !row.isSummaryRow && row[field + "_ids"]
        ? ["show-raw-data", "value"]
        : ["value"]
    },
    renderCell(row, isSummaryCol, field, isQuota) {
      let style = this.getStyle(field, row[field], row.isSummaryRow)
      return (
        <div
          class={this.getClass(row, isSummaryCol)}
          style={style && !style.includes("width") ? style : ""}
        >
          <div class="data-bar-wrapper">
            <div class="data-bar" style={style && style.includes("width") ? style : ""}></div>
          </div>
          <div
            class={this.getValueClass(row, field, isQuota)}
            v-tip
            vOn:click={() => this.goRawData(row, field, isQuota)}
          >
            {row[field] || row[field] === 0 ? row[field] : "-"}
          </div>
        </div>
      )
    },
    getStyle(fieldId, data, isSummaryRow) {
      let result = ""
      let rule = this.rules.find(rule => this.newColIdMap[rule.quota]?.includes(fieldId))
      if (rule === undefined || data === undefined || isSummaryRow) return
      let numberData = this.getNumber(data)
      switch (rule.type) {
        case "区间色":
          for (let condition of rule.conditions) {
            if (
              eval(
                (condition.startValue ? condition.startValue : -Infinity) +
                  condition.startMethod +
                  numberData
              ) &&
              eval(
                numberData +
                  condition.endMethod +
                  (condition.endValue ? condition.endValue : Infinity)
              )
            ) {
              result = "background:" + condition.background + "; color:" + condition.color
              break
            }
          }
          break
        case "渐变色": {
          let allData = this.getAllData(fieldId)
          let color = this.colorList.find(item => item.value === rule.colorScheme)?.color
          if (color) {
            let colors = gradient(color[0], color[1], allData.length)
            let index = allData.findIndex(item => item === numberData)
            result = "background:" + colors[index]
          }
          break
        }
        case "数据条": {
          let allData = this.getAllData(fieldId)
          let absData = allData.map(data => Math.abs(data))
          let max = this._.max(absData)
          let percent = allData.map(item => (Math.abs(item) / max) * 100 + "%")
          let index = allData.findIndex(item => item === numberData)
          let color = numberData <= 0 ? rule.negative : rule.positive
          result = "background:" + color + "; width:" + percent[index]
          break
        }
      }
      return result
    },
    getAllData(fieldId) {
      let allData = this.tableData
        .filter(item => !item.isSummaryRow)
        .map(item => item[fieldId])
        .filter(t => t)
        .map(item => this.getNumber(item))
      let sortFunc = (a, b) => naturalSort(a, b)
      return allData.sort(sortFunc)
    },
    renderHeader(field, title, isQuota, isSummaryCol) {
      if (isQuota)
        return (
          <div class="column-header">
            <span class="quota-title">
              <span class="wrapper">
                <span class="info" v-tip class={isSummaryCol ? "summary" : ""}>
                  {title}
                </span>
                {this.renderIcon(field)}
              </span>
            </span>
            {this.renderSort(field)}
          </div>
        )
      else
        return (
          <div class="column-header">
            <span v-tip class={isSummaryCol ? ["summary", "common-title"] : "common-title"}>
              {title}
            </span>
          </div>
        )
    },
    renderIcon(field) {
      let icon = this.sortIconMap[this.customSort.direction]
      return field === this.customSort.key && icon ? (
        icon.includes("svgo") ? (
          <svg-icon icon-class={icon}></svg-icon>
        ) : (
          <i class={icon}></i>
        )
      ) : (
        this.renderString()
      )
    },
    renderSort(field) {
      return !this.exportExcel ? (
        <dropdown-menu
          width="120"
          menu-list={this.menuList}
          vOn:click={menu => this.handleMenuClick(menu, field)}
          trigger="click"
          from="cross-table"
        >
          <span class="icon pointer">
            <svg-icon icon-class="svgo_arrow-down"></svg-icon>
          </span>
        </dropdown-menu>
      ) : (
        this.renderString()
      )
    },
    renderString(str) {
      return <span>{str}</span>
    },
    handleMenuClick(command, field) {
      this.customSort = {
        direction: command,
        key: field,
        internal: command.includes("group"),
        group: this.defaultSort.group
      }
      this.refreshData()
    },
    getPosition(title, fieldId, children) {
      if (children && children.length) return "center"
      let xAxisIds = this.xAxisList.filter(xAxis => xAxis.type === "ROW").map(xAxis => xAxis.id)
      if (xAxisIds.includes(fieldId)) return "left"
      else return "right"
    },
    goRawData(row, field, isQuota) {
      if (this.showRawData && isQuota && !row.isSummaryRow && row[field + "_ids"]) {
        const yAxis = this.yAxisList.find(yAxis => this.newColIdMap[yAxis.id]?.includes(field))
        const quota = this.quotas.find(quota => quota.id === yAxis?.fieldId)
        const quotaFilters = quota?.groupFilters || []
        localStorage.setItem(
          "chartDataFilter",
          JSON.stringify({ chartDataIds: row[field + "_ids"], quotaFilters })
        )
        const { href } = this.$router.resolve({
          name: "worksheet",
          params: { appId: this.appId, sheetId: this.sheetId },
          query: { boardFlag: true }
        })
        window.open(href, "_blank")
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pivot-vxe-table {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  .empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  .table {
    flex: 1;
    overflow: hidden;
    margin: 10px;
  }
  .table-pagination {
    height: 32px;
    margin-left: 10px;
    text-align: left;
  }
  .summary {
    font-weight: 500;
  }
  .column-header {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    .common-title {
      width: 100%;
      text-align: center;
      margin: 0 10px;
    }
    .quota-title {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      width: calc(100% - 5px);
      margin-right: 15px;
      margin-left: 10px;
      .wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        .icon {
          flex: 1;
        }
      }
    }
  }
  .icon {
    position: absolute;
    right: 6px;
    top: 5px;
  }
  ::v-deep {
    .ps__rail-x,
    .ps__rail-y {
      z-index: 10;
    }
    .vxe-table--fixed-left-wrapper {
      z-index: 10;
    }
    .vxe-cell {
      display: flex;
      align-items: center;
      padding: 0 !important;
      height: 36px !important;
      .cell {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 100%;
        width: 100%;
        padding-right: 10px;
        padding-left: 10px;
        position: relative;
        overflow: hidden;
        .data-bar-wrapper {
          position: absolute;
          left: 0;
          height: 18px;
          margin: 0 4px;
          width: calc(100% - 8px);
          .data-bar {
            height: 100%;
            width: 0;
            border-radius: 1px;
          }
        }
        .value {
          width: 100%;
          z-index: 10;
        }
        .show-raw-data {
          cursor: pointer;
        }
      }
    }
    .vxe-cell--title {
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .vxe-table--body-wrapper {
      overscroll-behavior: contain;
      &::-webkit-scrollbar {
        display: none; /* Chrome Safari */
      }
      -ms-overflow-style: none; /* IE 10+ */
      scrollbar-width: none; /* Firefox */
    }
    .vxe-table--body-wrapper {
      font-size: 13px;
      .vxe-tree--btn-wrapper {
        font-size: 12px; //树形加载按钮
      }
    }
    .vxe-table--header-wrapper {
      .vxe-header--row {
        .vxe-header--column {
          height: 36px;
          .vxe-cell {
            justify-content: center;
          }
        }
      }
    }
    .summary {
      font-weight: 500;
    }
  }
}
.export-class {
  position: absolute;
}
</style>
